import * as TabLib from '@radix-ui/react-tabs';
import clsx from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { Ref, createContext, forwardRef, useContext } from 'react';
import useMeasure from 'react-use-measure';

import styles, { TabVariants } from './tab.css';

type CommonProps = {
  children: React.ReactNode;
  className?: string;
};
type Root = CommonProps & TabVariants;
type List = {
  aria: string;
};
type Content = {
  id: string;
};

type Orientation = TabLib.RovingFocusGroupProps['orientation'];

// Create a custom context as Radix does not export the internally used context
// that manages data attributes.
const OrientationContext = createContext<Orientation>(undefined);

const VariantContext = createContext<TabVariants>(undefined);

const TabRoot = ({ children, className, hierarchy, ...rest }: Root & TabLib.TabsProps) => (
  <OrientationContext.Provider value={rest.orientation}>
    <VariantContext.Provider value={{ hierarchy }}>
      <TabLib.Root className={clsx(styles.TabRoot, className)} {...rest}>
        {children}
      </TabLib.Root>
    </VariantContext.Provider>
  </OrientationContext.Provider>
);
const TabList = ({ children, aria, className }: CommonProps & List) => {
  const variant = useContext(VariantContext);
  return (
    <TabLib.List className={clsx(styles.TabList(variant), className)} aria-label={aria}>
      {children}
    </TabLib.List>
  );
};
/**
 * Sets a data attribute to the Trigger contents (if a string), which is used to
 * prevent a layout shift when text is bolded on hover or active state changes.
 * If you pass a component as props, you'll need to handle this. See <TabLabel />
 * for an example of how.
 */
const TabTrigger = ({ children, id, className }: CommonProps & Content) => {
  const variant = useContext(VariantContext);
  return (
    <TabLib.Trigger
      className={clsx(styles.TabTrigger(variant), className)}
      value={id}
      data-text={typeof children === 'string' && children}
    >
      {children}
    </TabLib.Trigger>
  );
};

const animationDuration = 0.3;

const TabContentFadeIn = forwardRef(
  (
    {
      children,
      id,
      className,
      prefersReducedMotion,
      orientation,
    }: CommonProps & Content & { prefersReducedMotion: boolean | null; orientation: Orientation },
    ref: Ref<HTMLDivElement>,
  ) => {
    const variant = useContext(VariantContext);
    return (
      <TabLib.Content value={id} className={clsx(styles.TabContent(variant), className)} ref={ref}>
        <motion.div
          key={id}
          data-orientation={orientation}
          className={clsx(styles.TabContent(variant), className)}
          initial={{ opacity: prefersReducedMotion ? 1 : 0 }}
          animate={{
            opacity: 1,
          }}
          exit={{ opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : animationDuration, delay: animationDuration }}
        >
          {children}
        </motion.div>
      </TabLib.Content>
    );}
);

const TabContent = ({ id, ...props }: CommonProps & Content) => {
  const [ref, { height }] = useMeasure();
  const prefersReducedMotion = useReducedMotion();
  const orientation = useContext(OrientationContext);
  // Get the variant from the context, as we want to selectively apply a dynamic
  // height to the tab content. This is to avoid layout shifts that occur in page layouts.
  const variant = useContext(VariantContext);  
  const isCardVariant = variant?.hierarchy === 'card' || variant?.hierarchy === undefined;
  const tabContentHeight = isCardVariant ? height || 'auto' : 'auto';

  // TODO: animation for vertical layout, if required to manage scroll position jumps.
  if (orientation === 'vertical') {
    return (
      <TabContentFadeIn
        {...props}
        id={id}
        ref={ref}
        orientation={orientation}
        prefersReducedMotion={prefersReducedMotion}
      />
    );
  }

  return (
    // Animates a smooth scroll should the content height vary between tabs
    // and change the scroll position.
    <motion.div
      key={id}
      data-orientation={orientation}
      animate={{
        height: prefersReducedMotion ? 'auto' : tabContentHeight,
      }}
      transition={{ duration: animationDuration }}
    >
      <TabContentFadeIn
        {...props}
        id={id}
        ref={ref}
        orientation={orientation}
        prefersReducedMotion={prefersReducedMotion}
      />
    </motion.div>
  );
};

export const Tab = Object.assign(TabRoot, {
  List: TabList,
  Item: TabTrigger,
  Content: TabContent,
});

